/**
 * 认证存储服务
 *
 * 安全说明：
 * - 令牌（Access/Refresh）绝不应存储在 localStorage 中
 * - 仅持久化非敏感的元数据（用户信息）
 * - Access Token：存储在内存中（Pinia Store）
 * - Refresh Token：存储在 HttpOnly Cookie 中（后端管理）
 */

import type { SessionMetadata } from './contracts';

const STORAGE_KEY = 'puppy-cad-session-metadata';

// 处理 bigint 序列化
const replacer = (_key: string, value: any) => {
  if (typeof value === 'bigint') {
    return value.toString();
  }
  return value;
};

// 处理 bigint 反序列化
const reviver = (key: string, value: any) => {
  if ((key === 'id' || key === 'parentUserId') && typeof value === 'string' && /^\d+$/.test(value)) {
    return BigInt(value);
  }
  return value;
};

export const authStorage = {
  /**
   * 获取会话元数据（用户信息、时间戳）
   * 不包含令牌
   */
  getMetadata(): SessionMetadata | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw, reviver) as SessionMetadata;
    } catch (e) {
      console.error('[Auth] 解析会话元数据失败', e);
      return null;
    }
  },

  /**
   * 保存会话元数据（用户信息、时间戳）
   * 绝不在此保存令牌
   */
  setMetadata(metadata: SessionMetadata) {
    try {
      const raw = JSON.stringify(metadata, replacer);
      localStorage.setItem(STORAGE_KEY, raw);
    } catch (e) {
      console.error('[Auth] 保存会话元数据失败', e);
    }
  },

  /** 清除会话元数据 */
  clearMetadata() {
    localStorage.removeItem(STORAGE_KEY);
  },

  /**
   * 旧版存储迁移：将 'puppy-cad-session' 中的用户信息迁移到新 key
   *
   * TODO: 待评估是否可删除 —— 如果旧版 localStorage key 已不存在于任何生产环境，
   * 可安全移除此函数及 stores/auth.ts 中的调用。
   */
  migrateFromLegacy() {
    const legacyKey = 'puppy-cad-session';
    const legacy = localStorage.getItem(legacyKey);
    if (legacy) {
      try {
        const parsed = JSON.parse(legacy);
        // 仅提取安全的元数据
        if (parsed.user) {
          this.setMetadata({
            user: parsed.user,
            expiresAt: parsed.expiresAt,
          });
        }
      } catch {
        // 迁移失败时静默忽略，旧数据将在下方被清除
      }
      // 清除旧存储
      localStorage.removeItem(legacyKey);
    }
  },
};

